/**
 * Badge criteria evaluation (R7.10, Rewards v1 + Phase 2b + Phase 2c —
 * `docs/specs/phase-2a-rewards.md` RW-8/RW-9, `docs/specs/
 * phase-2b-habits.md` §5/AC-B22, `docs/specs/phase-2c-weekly-review.md`
 * WR-20/AC-W23). Pure function of (event log, badge definitions, today) —
 * no I/O, no stored earned-state (AC-R-S3/AC-B-S3/AC-W-S3). `today` is
 * required (not optional) so `checkin_streak_weeks` can measure the live
 * streak without reaching for `Date.now()` (ADR-0001 rule 2) -- every other
 * criterion ignores it.
 *
 * Scope: `accepted_recalibrations` and `action_completions` (Rewards v1,
 * phase-2a-rewards.md §1 RW-8), `checkin_streak_weeks` (activated
 * phase-2b, AC-B22 — `RewardSnapshot.streakDays`'s week-granular badge
 * counterpart), and `weekly_reviews` (activated this pass, WR-20 — counts
 * distinct `review.completed` weeks, the same collapse-by-weekStart rule
 * `xp.ts`'s `computeReviewXpAwards` uses). `habit_completions` and
 * `clean_weeks` reference a `HabitId` and are structurally unearnable until
 * a per-habit badge UI exists to create them (still no starter template
 * references a real HabitId); they evaluate to a permanent 0/target-progress,
 * never earned, never throwing — "ignore what you don't understand, never
 * crash" extended to referential gaps, not just unknown event types.
 */

import type {
  BadgeCriterion,
  BadgeDefinition,
  BadgeId,
  CompassEvent,
  IsoDate,
} from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { addDaysToDate } from '../habits/week';
import { computeStreakDays } from './streak';

export type BadgeState =
  | { readonly earned: true; readonly earnedOn: IsoDate }
  | {
      readonly earned: false;
      readonly progress: { readonly current: number; readonly target: number };
    };

export interface BadgeEvaluation {
  readonly badgeId: BadgeId;
  readonly state: BadgeState;
}

/**
 * RW-3 mirrors here: only accepted forecast suggestions count toward
 * `accepted_recalibrations` in Rewards v1. `phase-2a-rewards.md` §3.1 flags
 * that whether this criterion should ALSO count habit-target adjustments
 * (`habit.target_adjusted`, source: 'review_suggestion') is an open product
 * question once habits exist — not decided here, and moot today since no
 * `habit.target_adjusted` event can exist in a habit-less log. When habits
 * ship, this function's `accepted_recalibrations` branch is the one place
 * to extend, per that same escalation.
 */
function countAcceptedRecalibrations(
  events: readonly CompassEvent[],
): number {
  const active = excludingReverted(events);
  return eventsOfTypes(active, ['objective.target_date_adjusted']).filter(
    (event) => event.source === 'forecast_suggestion',
  ).length;
}

function countActionCompletions(events: readonly CompassEvent[]): number {
  const active = excludingReverted(events);
  return eventsOfTypes(active, ['action.completed']).length;
}

/** `checkin_streak_weeks`'s "current" is whole weeks of the live day-streak (AC-B22, floor division). */
function countStreakWeeks(events: readonly CompassEvent[], today: string): number {
  return Math.floor(computeStreakDays(events, today) / 7);
}

/** `weekly_reviews`'s "current" is the count of DISTINCT reviewed weeks (WR-20) — mirrors `xp.ts`'s `computeReviewXpAwards` collapse-by-weekStart rule so badge progress and XP never disagree about "how many reviews". */
function countCompletedReviews(events: readonly CompassEvent[]): number {
  const active = excludingReverted(events);
  const weeks = new Set(eventsOfTypes(active, ['review.completed']).map((e) => e.weekStart));
  return weeks.size;
}

/**
 * Evaluate one criterion against the log, returning the raw current count
 * and whether it's a criterion kind this engine can actually feed (habit-
 * or review-dependent criteria are always "0 of target", not an error —
 * they simply have no evaluable signal yet).
 */
function evaluateCriterion(
  criterion: BadgeCriterion,
  events: readonly CompassEvent[],
  today: string,
): { readonly current: number; readonly target: number } {
  switch (criterion.kind) {
    case 'accepted_recalibrations':
      return {
        current: countAcceptedRecalibrations(events),
        target: criterion.count,
      };
    case 'action_completions':
      return {
        current: countActionCompletions(events),
        target: criterion.count,
      };
    case 'checkin_streak_weeks':
      return {
        current: countStreakWeeks(events, today),
        target: criterion.weeks,
      };
    case 'weekly_reviews':
      return {
        current: countCompletedReviews(events),
        target: criterion.count,
      };
    // Structurally unearnable this pass (no habit can exist for a starter
    // template to reference yet) — permanently 0-progress, never throws.
    case 'habit_completions':
      return { current: 0, target: criterion.count };
    case 'clean_weeks':
      return { current: 0, target: criterion.weeks };
  }
}

/**
 * The `occurredOn` of the Nth (1-indexed by `target`) qualifying event, used
 * as the badge's `earnedOn` date — "the date of the Nth completion"
 * (AC-R11). Returns null if fewer than `target` qualifying events exist.
 *
 * `checkin_streak_weeks` has no discrete "qualifying event" list (it is a
 * derived run-length, not a count of events) — its `earnedOn` is instead the
 * calendar date the streak's `target * 7`th consecutive day fell on, found
 * by walking back from `today` (mirrors `computeStreakDays`'s own walk).
 */
function nthQualifyingDate(
  criterion: BadgeCriterion,
  events: readonly CompassEvent[],
  target: number,
  today: string,
): IsoDate | null {
  const active = excludingReverted(events);

  if (criterion.kind === 'checkin_streak_weeks') {
    return nthStreakDayDate(active, target * 7, today);
  }

  if (criterion.kind === 'weekly_reviews') {
    // One qualifying "event" per DISTINCT weekStart (a week reviewed twice
    // -- e.g. a defensive re-completion, AC-W1's idempotency case -- must
    // not count as two toward "the Nth review"), keeping the FIRST
    // (canonical order) review.completed event per week as that week's
    // representative date.
    const seen = new Set<string>();
    const sorted = [...eventsOfTypes(active, ['review.completed'])].sort((a, b) =>
      a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0,
    );
    const distinct = sorted.filter((event) => {
      if (seen.has(event.weekStart)) return false;
      seen.add(event.weekStart);
      return true;
    });
    const nth = distinct[target - 1];
    return nth ? nth.occurredOn : null;
  }

  let qualifying: readonly CompassEvent[];
  switch (criterion.kind) {
    case 'accepted_recalibrations':
      qualifying = eventsOfTypes(active, [
        'objective.target_date_adjusted',
      ]).filter((event) => event.source === 'forecast_suggestion');
      break;
    case 'action_completions':
      qualifying = eventsOfTypes(active, ['action.completed']);
      break;
    default:
      return null;
  }

  // Canonical log order = lexicographic eventId = chronological creation
  // order (ADR-0002/0003) — the Nth element in that order is "the Nth
  // qualifying event", matching AC-R11's "date of the Nth completion".
  const sorted = [...qualifying].sort((a, b) =>
    a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0,
  );
  const nth = sorted[target - 1];
  return nth ? nth.occurredOn : null;
}

/** The calendar date of the Nth consecutive check-in day, walking back from `today`. */
function nthStreakDayDate(
  active: readonly CompassEvent[],
  n: number,
  today: string,
): IsoDate | null {
  const checkinDates = new Set<string>(
    eventsOfTypes(active, ['checkin.completed']).map((e) => e.occurredOn as string),
  );
  const yesterday = addDaysToDate(today, -1);
  let cursor = checkinDates.has(today) ? today : yesterday;

  let count = 0;
  while (checkinDates.has(cursor)) {
    count += 1;
    if (count === n) return cursor as IsoDate;
    cursor = addDaysToDate(cursor, -1);
  }
  return null;
}

/**
 * Evaluate every badge definition against the log. Order matches the input
 * `badges` order (callers assemble active vs. deferred sets upstream, per
 * RW-9 / AC-R13 — this function has no opinion on which badges are "live").
 */
export function evaluateBadges(
  badges: readonly BadgeDefinition[],
  events: readonly CompassEvent[],
  today: string,
): readonly BadgeEvaluation[] {
  return badges.map((badge) => {
    const { current, target } = evaluateCriterion(badge.criterion, events, today);
    if (current >= target && target > 0) {
      const earnedOn = nthQualifyingDate(badge.criterion, events, target, today);
      if (earnedOn) {
        return { badgeId: badge.badgeId, state: { earned: true, earnedOn } };
      }
    }
    return {
      badgeId: badge.badgeId,
      state: { earned: false, progress: { current, target } },
    };
  });
}

/**
 * Badge IDs newly earned by the MOST RECENT qualifying event only — used to
 * populate `CelebrationCue.badgesEarned` for "this command's award included
 * a badge" (AC-R11: "the next `completeAction` call includes its `badgeId`
 * in that call's `CelebrationCue.badgesEarned`"). Compares evaluation
 * before vs. after the log gained `latestEventIds`.
 */
export function badgesNewlyEarned(
  badges: readonly BadgeDefinition[],
  eventsBefore: readonly CompassEvent[],
  eventsAfter: readonly CompassEvent[],
  today: string,
): readonly BadgeId[] {
  const before = new Map(
    evaluateBadges(badges, eventsBefore, today).map((e) => [e.badgeId, e.state.earned]),
  );
  const after = evaluateBadges(badges, eventsAfter, today);
  return after
    .filter((e) => e.state.earned && before.get(e.badgeId) !== true)
    .map((e) => e.badgeId);
}
